import {
    FormValues,
    RuleConditionType,
    Rule,
    RuleEvaluator,
    FormConfig
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getElementFromElementId } from '../helpers/getElementFromElementId';
import { getValue } from '../components/values/Get';

export const isElementVisible = (
    formConfig: FormConfig,
    formValues: FormValues,
    elementId: string,
    visibleResults: Record<string, boolean> = {}
) => {
    const conditionGroupResults = getConditionGroupResults(formConfig, formValues, elementId, visibleResults);
    // AND all rule groups
    const allConditionGroupsAreTrue = conditionGroupResults.every((result: boolean) => result === true);

    return allConditionGroupsAreTrue;
};

/**
 * The 'hidden' element attribute can be used to include an element value in the submitted form payload, but without
 * displaying/rendering the element in the GUI. An example is the dateOfLoss element, which for some forms is not
 * visible to the customer/user (e.g. for certain travel cancellation scenarios) but still required by the CA backend
 * when processing the claim.
 *
 * A hidden element value can be set by code in the conditional page, e.g. formValues["myHiddenElementId"] = "some value"
 *
 * @param formConfig
 * @param elementId
 * @returns
 */
export const isHiddenElement = (formConfig: FormConfig, elementId: string): boolean => {
    const element = getElementFromElementId(formConfig, elementId);

    return element.hidden === undefined ? false : element.hidden;
};

const getConditionGroupResults = (
    formConfig: FormConfig,
    formValues: FormValues,
    elementId: string,
    visibleResults: Record<string, boolean> = {}
) => {
    const element = getElementFromElementId(formConfig, elementId);
    const { displayCondition } = element;

    // ALL displayCondiotion[] MUST be true
    //
    // (element-1 && element-2) && (element-3 || element-4)
    //
    //
    // const displayCondition = [
    //     {
    //         operator: 'and',
    //         rules: [
    //             { elementId: 'firstName', value: 'Peter', evaluator: '=' },
    //             { elementId: 'lastName', value: 'Parker', evaluator: '=' }
    //         ]
    //     },

    //     {
    //         operator: 'or',
    //         rules: [
    //             { elementId: 'isDriver', value: true, evaluator: '=' },
    //             { elementId: 'hasPassenger', value: true, evaluator: '=' }
    //         ]
    //     }
    // ];
    //
    // Explanation:
    //     (
    //           firstName === 'Peter'
    //       AND
    //           lastName === 'Parker'
    //     )
    // AND
    //     (
    //           isDriver === true
    //       OR
    //           hasPassenger === true
    //     )
    //
    // Evaluator type 'propertyIsIdentical' is only for a value which is an object with properties

    const conditionGroupResults = displayCondition.map((conditionSet: RuleConditionType) => {
        const { operator, rules } = conditionSet;

        const rulesResult = rules.map((rule: Rule) => {
            const { elementId: ruleElementId, evaluator, value: ruleValue } = rule;

            // A is always shown
            // B is shown if A has the value '123'
            // C is shown if B is visible
            //
            // If B is NOT visible, then C should also NOT be shown
            if (!isElementVisible(formConfig, formValues, ruleElementId, visibleResults)) {
                return false;
            }

            // A particular field that is validated against
            // might not have been shown and thus be undefined
            const formValue = getValue(formConfig, formValues, ruleElementId);

            if (formValue === undefined) {
                return false;
            }
            const result = evaluateRule(evaluator, ruleValue, formValue);
            return result;
        });

        // This returns the current *conditionGroupResult* result
        switch (operator) {
            case 'and':
                const isAllTrue = rulesResult.every((value: boolean) => value === true);
                visibleResults[elementId] = isAllTrue;
                return isAllTrue;

            case 'or':
                const isSomeTrue = rulesResult.some((value: boolean) => value === true);
                visibleResults[elementId] = isSomeTrue;
                return isSomeTrue;

            default:
                throw new Error('Unknown operator: ' + operator);
        }
    });

    return conditionGroupResults;
};

const evaluateRule = (evaluator: RuleEvaluator, ruleValue: any, value: any) => {
    const formFieldValue = checkValueType(value);

    switch (evaluator) {
        // @TODO
        //   - Something about number and string comparison?
        case '=':
        case 'isIdentical':
            return formFieldValue === ruleValue;
        case '!=':
        case 'isNotIdentical':
            return formFieldValue !== ruleValue;

        case '>':
            return formFieldValue > ruleValue;

        case '>=':
            return formFieldValue >= ruleValue;

        case '<':
            return formFieldValue < ruleValue;

        case '<=':
            return formFieldValue <= ruleValue;

        case 'beginsWith':
            return new RegExp(`^${ruleValue}`).test(formFieldValue);

        case 'doesNotBeginWith':
            return !new RegExp(`${ruleValue}$`).test(formFieldValue);

        case 'endsWith':
            return new RegExp(`${ruleValue}$`).test(formFieldValue);

        case 'doesNotEndWith':
            return !new RegExp(`${ruleValue}$`).test(formFieldValue);

        case 'contains':
            return new RegExp(`${ruleValue}`).test(formFieldValue);

        case 'doesNotContain':
            return !new RegExp(`${ruleValue}`).test(formFieldValue);

        case 'between':
            return formFieldValue >= ruleValue.minValue && formFieldValue <= ruleValue.maxValue;

        case 'notBetween':
            return formFieldValue < ruleValue.minValue && formFieldValue > ruleValue.maxValue;

        case 'inList': {
            return ruleValue.includes(formFieldValue);
        }

        case 'valueContains':
            return formFieldValue.includes(ruleValue);

        case 'propertyIsIdentical':
            return formFieldValue[ruleValue.property] === ruleValue.value;
        default:
            throw new Error('Invalid evaluator: ' + evaluator);
    }
};

// @TODO - It's only for object values which have a .value property.
// Need to be handled correct
const checkValueType = (value: any) => {
    if (value instanceof Object && value.hasOwnProperty('value')) {
        return value.value;
    } else if (Array.isArray(value) && value[0] instanceof Object && value[0].hasOwnProperty('value')) {
        // may return an array of {label: '', value: ''}
        // e.g. checkboxes
        return value.map(({ label, value }) => value);
    } else {
        return value;
    }
};
